import { useEffect, useMemo } from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import { useRouter, useParams, useSearchParams, usePathname } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { Store } from "~/bff/types/Store";
import { DataLayerData, EventsNames, EventTypes } from "~/constants/data-layer";
import { Params } from "~/constants/request";
import { getCountryCode } from "~/helpers/country/get-country-code";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { Nullable } from "~/types/general.types";

export const useHandleRedirectFromStoresInPage = (
  store: Nullable<Store> | undefined,
) => {
  const router = useRouter();
  const { locale } = useParams<GlobalPathParams>();
  const searchParams = useSearchParams();
  const query = useMemo(
    () => new URLSearchParams(Array.from(searchParams.entries())),
    [searchParams],
  );
  const pathname = usePathname();
  const isRedirectFromStoresInPage =
    query.get(Params.REDIRECT_FROM) === "stores-in-city";

  const isUserAuthenticated = useIsAuthenticated();
  const countryCode = getCountryCode(locale);

  useEffect(() => {
    if (!isRedirectFromStoresInPage) {
      return;
    }

    query.delete(Params.REDIRECT_FROM);

    router.push(`${pathname}?${query.toString()}`);

    pushDataLayer({
      [DataLayerData.event]: EventsNames.storeLocatorStoreSelected,
      [DataLayerData.siteCountry]: countryCode,
      [DataLayerData.type]: EventTypes.storeLocator,
      [DataLayerData.pageCategory]: "store_locator",
      [DataLayerData.loggedInStatus]: isUserAuthenticated,
      [DataLayerData.storeLocatorStoreSelected]: store?.geomodifier ?? "",
    });
  }, [
    countryCode,
    isRedirectFromStoresInPage,
    isUserAuthenticated,
    pathname,
    query,
    router,
    store?.geomodifier,
  ]);
};
